import { useEffect, useState } from 'react';
import {
    Container, Typography, Box, Button, TextField, Alert,
    CircularProgress, Switch, Stack,
} from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';
import { checkUserGroup } from '../utils/authUtils';
import { Surface, SectionHeader } from '../components/primitives';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NotificationsIcon from '@mui/icons-material/Notifications';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Account = () => {
    const [profile, setProfile] = useState({ name: '', email: '', phone_number: '', jwtToken: '' });
    const [isPremiumUser, setIsPremiumUser] = useState(false);
    const [isCompUser, setIsCompUser] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [notifLoading, setNotifLoading] = useState(false);
    const stripe = useStripe();
    const elements = useElements();

    const fetchAttributes = async (forceRefresh = false) => {
        try {
            const session = await fetchAuthSession({ forceRefresh });
            if (session?.tokens?.idToken) {
                const jwtToken = session.tokens.idToken.toString();
                const groupInfo = await checkUserGroup();
                setIsPremiumUser(groupInfo.isPro);
                setIsCompUser(groupInfo.isComp);

                if (groupInfo.isPro) {
                    try {
                        const notifResponse = await fetch('https://api.capitolgains.io/user/notifications', {
                            headers: { Authorization: `Bearer ${jwtToken}` },
                        });
                        if (notifResponse.ok) {
                            const notifData = await notifResponse.json();
                            setNotificationsEnabled(notifData.notifications_enabled ?? true);
                        }
                    } catch (err) {
                        console.error('Error fetching notification preferences:', err);
                    }
                }

                setProfile({
                    name: session.tokens.idToken.payload.name || '',
                    email: session.tokens.idToken.payload.email || '',
                    phone_number: session.tokens.idToken.payload.phone_number || '',
                    jwtToken,
                });
            }
        } catch (err) {
            console.error('Error fetching user attributes', err);
        }
    };

    useEffect(() => {
        fetchAttributes();
    }, []);

    const handleNotificationsToggle = async (event) => {
        const enabled = event.target.checked;
        setNotificationsEnabled(enabled);
        setNotifLoading(true);
        try {
            const response = await fetch('https://api.capitolgains.io/user/notifications', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${profile.jwtToken}` },
                body: JSON.stringify({ notifications_enabled: enabled }),
            });
            if (!response.ok) throw new Error('Failed to save notification preference');
        } catch (err) {
            console.error('Error saving notification preferences:', err);
            setNotificationsEnabled(!enabled);
        } finally {
            setNotifLoading(false);
        }
    };

    const handleUpgrade = async () => {
        setLoading(true);
        try {
            if (!profile.jwtToken || !stripe || !elements) {
                setLoading(false);
                return;
            }
            const cardElement = elements.getElement(CardElement);
            const { error, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card: cardElement });
            if (error) {
                console.error('Error creating payment method:', error.message);
                setLoading(false);
                return;
            }
            const response = await fetch('https://api.capitolgains.io/user/create-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${profile.jwtToken}` },
                body: JSON.stringify({ email: profile.email, paymentMethodId: paymentMethod.id }),
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const { message } = await response.json();
            if (message.includes('Subscription created, payment confirmed')) {
                await fetchAttributes(true);
            }
        } catch (error) {
            console.error('Error during upgrade:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container sx={{ pt: 6, pb: 8, maxWidth: '720px !important' }}>
            <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                Account
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                Manage your profile, subscription and alert preferences.
            </Typography>

            {/* Profile */}
            <SectionHeader icon={AccountCircleIcon}>Profile</SectionHeader>
            <Surface sx={{ mb: 4 }}>
                <Stack spacing={2}>
                    {profile.name && (
                        <TextField label="Name" value={profile.name} fullWidth InputProps={{ readOnly: true }} />
                    )}
                    {profile.email && (
                        <TextField label="Email" value={profile.email} fullWidth InputProps={{ readOnly: true }} />
                    )}
                    {profile.phone_number && (
                        <TextField label="Phone" value={profile.phone_number} fullWidth InputProps={{ readOnly: true }} />
                    )}
                </Stack>
            </Surface>

            {/* Subscription */}
            <SectionHeader icon={CreditCardIcon}>Subscription</SectionHeader>
            <Surface sx={{ mb: 4 }}>
                {isCompUser ? (
                    <Alert severity="info" sx={{ mb: 0 }}>
                        You have <strong>complimentary Pro access</strong>. No subscription or billing required.
                    </Alert>
                ) : isPremiumUser ? (
                    <>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            You're a <strong>Pro</strong> subscriber — thanks for your support.
                        </Alert>
                        <Button variant="outlined" component={Link} to="/manage-subscription">
                            Manage Subscription
                        </Button>
                    </>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            You're on the <strong>Free</strong> plan. Upgrade to Pro for $8/month for full trade history,
                            real-time disclosures, and member watchlist alerts.
                        </Alert>
                        <Box sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 1,
                            bgcolor: 'custom.surface.subtle',
                            border: t => `1px solid ${t.palette.custom.border.default}`,
                        }}>
                            <CardElement
                                options={{
                                    style: {
                                        base: {
                                            color: '#ffffff',
                                            fontSize: '15px',
                                            fontFamily: 'Inter, sans-serif',
                                            '::placeholder': { color: '#6b7280' },
                                        },
                                        invalid: { color: '#ef4444' },
                                    },
                                }}
                            />
                        </Box>
                        <Button variant="contained" onClick={handleUpgrade} disabled={loading}>
                            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Upgrade to Pro'}
                        </Button>
                    </>
                )}
            </Surface>

            {/* Notifications (premium only) */}
            {isPremiumUser && (
                <>
                    <SectionHeader icon={NotificationsIcon}>Notifications</SectionHeader>
                    <Surface>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                    Email trade alerts
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                    Get an email when a member you're tracking makes a new trade.
                                </Typography>
                            </Box>
                            <Switch
                                checked={notificationsEnabled}
                                onChange={handleNotificationsToggle}
                                disabled={notifLoading}
                            />
                        </Box>
                    </Surface>
                </>
            )}
        </Container>
    );
};

const AccountWrapper = () => (
    <Elements stripe={stripePromise}>
        <Account />
    </Elements>
);

export default AccountWrapper;
